import { Options } from '@/enums/options.enum';
import { TableName } from '@/enums/tableName';
import { OptionValue } from '@/interfaces/optionValue';
import { View } from '@/interfaces/view';
import { post } from '@/resources/rest.resource';
import { ViewResource, viewResource } from '@/resources/view.resource';
import { tryParseInt } from '@/utils/number';
import store from '../store';
import { EntityModule } from './entity.module';

/**
 * Moduł widoków
 *
 * @export
 * @class ViewModule
 * @extends {EntityModule<View, ViewResource>}
 */
export class ViewModule extends EntityModule<View, ViewResource> {
  constructor() {
    super(viewResource);
    this.state = {
      ...this.state,
      previewHtml: '',
      previewData: undefined
    } as any;

    this.mutations = {
      ...this.mutations,
      setPreviewHtml(state: any, html: string): void {
        state.previewHtml = html;
      },
      setPreviewData(state: any, data: any): void {
        state.previewData = data;
      }
    };
    this.actions = {
      ...this.actions,

      /**
       * Pobiera listę widoków po identyfikatorach
       *
       * @param {*} context
       * @param {number[]} ids
       * @returns {Promise<View[]>}
       */
      listByIds: (context: any, ids: number[]): Promise<View[]> => {
        return this.resource.listByIds(ids).then(list => {
          context.commit('saveList', list);
          return list;
        });
      },

      /**
       * Pobiera listę widoków na podstawie opcji statusu
       *
       * @param {*} context
       * @param {number} statusId
       * @returns {Promise<View[]>}
       */
      listByStatusOption: (context: any, { statusId, multipleOnly }): Promise<View[]> => {
        if (!statusId) {
          context.commit('saveList', []);
          return Promise.resolve([]);
        }
        return store
          .dispatch('optionValue/getByCode', {
            code: Options.VIEWS,
            entityType: TableName.status,
            entityId: statusId.toString()
          })
          .then((optionValue: OptionValue) =>
            optionValue.value
              .split(',')
              .map(id => tryParseInt(id))
              .filter(Boolean)
          )
          .then(ids => {
            if (ids?.length) {
              return context.dispatch('list', { ids, multipleOnly });
            }
            context.commit('saveList', []);
            return [];
          });
      },

      /**
       * Zapisuje wygenerowany widok jako załącznik
       *
       * @param {*} context
       * @param {*} data
       * @returns {Promise<number>}
       */
      saveAsAttachment: (context: any, data: any): Promise<number> => {
        return this.resource.saveAsAttachment(data);
      },

      /**
       * Zapisuje widok wygenerowany grupowo
       *
       * @param {*} context
       * @param {*} data
       * @returns {Promise<number>}
       */
      saveGroupAsAttachment: (context: any, data: any): Promise<number> => {
        return this.resource.saveGroupAsAttachment(data);
      },

      /**
       * Zapisuje wygenerowany widok jako załącznik i przesyła do MDOK
       *
       * @param {*} context
       * @param {*} data
       * @returns {Promise<number>}
       */
      sendToMDOK: (context: any, data: any): Promise<number> => {
        return this.resource.sendToMDOK(data);
      },

      /**
       * Zapisuje widok wygenerowany grupowo i przesyła do MDOK
       *
       * @param {*} context
       * @param {*} data
       * @returns {Promise<number>}
       */
      sendGroupToMDOK: (context: any, data: any): Promise<number> => {
        return this.resource.sendGroupToMDOK(data);
      },

      /**
       * Zapisuje wyedytowany widok jako załącznik
       *
       * @param {*} context
       * @param {*} data
       * @returns {Promise<number>}
       */
      saveEditedHtmlAsAttachment: (context: any, data: any): Promise<number> => {
        return this.resource.saveEditedHtmlAsAttachment(data);
      },

      /**
       * Zapisuje wyedytowany widok do MDOK
       *
       * @param {*} context
       * @param {*} data
       * @returns {Promise<number>}
       */
      saveEditedHtmlToMDOK: (context: any, data: any): Promise<number> => {
        return this.resource.saveEditedHtmlToMDOK(data);
      },

      /**
       * Pobieranie podglądu widoku
       *
       * @param {*} context
       * @param {*} data
       * @returns {Promise<Response>}
       */
      getPreview: (context: any, data: any): Promise<string> => {
        return post(data.url, data.body)
          .then(response => response.text())
          .then(html => {
            context.commit('setPreviewHtml', html);
            return html;
          });
      }
    };
  }
}

export default new ViewModule();
